import React from 'react';
import { AgentExecutionFlow, ScoredResult, ExecutionEvent, ClarificationRequestedEvent, OrchestratorDecisionEvent } from '../types';
import { MessageBubble } from './MessageBubble';
import { ExecutionScoreCard } from './ExecutionScoreCard';
import { OpinionDetailCard } from './OpinionDetailCard';
import { SearchParametersDisplay } from './SearchParametersDisplay';
import { ClarificationDialog } from './ClarificationDialog';
import { Loader2, Award } from 'lucide-react';
import { NoDataCard } from './NoDataCard';

interface SimplifiedProviderColumnProps {
  providerName: string;
  providerId?: string;
  executionFlow: AgentExecutionFlow | undefined;
  isLoading: boolean;
  columnWidth: string;
  scoreData?: ScoredResult;
  evaluationExecutionId?: string;
  hideHeader?: boolean;
  executionEvents?: ExecutionEvent[];
  onSubmitClarification?: (executionId: string, userResponse: string) => Promise<void>;
}

export const SimplifiedProviderColumn: React.FC<SimplifiedProviderColumnProps> = ({
  providerName,
  executionFlow,
  isLoading,
  columnWidth,
  scoreData,
  hideHeader = false,
  executionEvents = [],
  onSubmitClarification
}) => {
  const isProviderLoading = isLoading || executionFlow?.status === 'inProgress';
  
  const formatPercent = (value: number) => `${Math.round(value * 100)}%`;
  
  const getAccuracyColor = (accuracy: number) => {
    if (accuracy >= 0.90) return 'bg-green-100 text-green-700 border-green-300';
    if (accuracy >= 0.75) return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    return 'bg-red-100 text-red-700 border-red-300';
  };

  // Detect clarification events
  const clarificationEvent = executionEvents.find(
    (e): e is ClarificationRequestedEvent => e.eventType === 'ClarificationRequested'
  );
  const clarificationReceived = executionEvents.some(e => e.eventType === 'ClarificationReceived');
  const [clarificationDismissed, setClarificationDismissed] = React.useState(false);

  // Fallback: derive clarification data from OrchestratorDecision when no ClarificationRequested event
  const orchestratorDecisionEvent = executionEvents.find(
    (e): e is OrchestratorDecisionEvent => e.eventType === 'OrchestratorDecision'
  );
  const needsClarificationFromDecision = !clarificationEvent
    && executionFlow?.status === 'needsClarification'
    && orchestratorDecisionEvent?.decision?.decision === 'need_explanation';

  // Unified clarification data
  const clarificationData = clarificationEvent
    ? { reason: clarificationEvent.reason, originalQuery: clarificationEvent.originalQuery, questions: clarificationEvent.questions }
    : needsClarificationFromDecision
      ? { reason: orchestratorDecisionEvent!.decision.reason ?? '', originalQuery: orchestratorDecisionEvent!.query, questions: orchestratorDecisionEvent!.decision.questions }
      : null;

  const showClarificationDialog = clarificationData && !clarificationReceived && !clarificationDismissed;

  const handleClarificationSubmit = async (response: string) => {
    if (onSubmitClarification && executionFlow?.executionId) {
      await onSubmitClarification(executionFlow.executionId, response);
    }
  };

  const handleClarificationCancel = () => {
    setClarificationDismissed(true);
  };

  // Extract search parameters from execution flow
  const searchParameters = executionFlow?.searchContexts?.[0];
  
  // Get opinions from searchedOpinions
  const opinions = executionFlow?.searchedOpinions || [];
  
  return (
    <div 
      data-component="SimplifiedProviderColumn" 
      className="flex-shrink-0 border-r border-gray-200 bg-white flex flex-col h-full" 
      style={{ width: columnWidth }}
    >
      {/* Minimal Header - Provider name and accuracy only */}
      {!hideHeader && (
        <div className="sticky top-0 bg-white border-b border-gray-200 p-3 z-10">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-lg font-semibold text-gray-900">{providerName}</h3>
            {scoreData && (
              <div className={`px-3 py-1.5 rounded-lg text-sm font-bold flex items-center gap-1.5 border ${getAccuracyColor(scoreData.accuracy)}`}>
                <Award className="w-3.5 h-3.5" />
                {formatPercent(scoreData.accuracy)}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Search Parameters */}
        {searchParameters && (
          <div data-flow="search-parameters" className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <SearchParametersDisplay
              vectorQueryText={searchParameters.vectorQueryText || []}
              contraryQueryText={searchParameters.contraryQueryText}
              maxResults={searchParameters.maxResults}
              maxPassagesPerDocument={searchParameters.maxPassagesPerDocument}
              filters={searchParameters.filters}
              orchestratorDecision={executionFlow?.orchestratorDecision?.decision}
            />
          </div>
        )}

        {/* Found Opinions - Using OpinionDetailCard */}
        {opinions.length > 0 && (
          <div data-flow="opinions">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">
              Opinions Used ({opinions.length})
            </h4>
            <div className="space-y-3">
              {opinions.map((opinion) => (
                <OpinionDetailCard key={opinion.id} opinion={opinion} compact={true} />
              ))}
            </div>
          </div>
        )}

        {/* AI Response - hidden for noData status since NoDataCard handles display */}
        {executionFlow?.finalResponse?.answer && executionFlow?.status !== 'noData' && (
          <div data-flow="response">
            <MessageBubble
              message={{
                id: executionFlow.executionId,
                type: 'assistant',
                content: executionFlow.finalResponse.answer,
                timestamp: new Date(executionFlow.startTime),
                isComplete: true
              }}
              citations={executionFlow.finalResponse.citations}
              paragraphs={executionFlow.finalResponse.paragraphs}
              opinions={executionFlow.searchedOpinions}
            />
          </div>
        )}

        {/* Scoring Section - Only show for successful completions */}
        {executionFlow?.executionId && executionFlow?.status === 'completed' && executionFlow?.finalResponse && (
          <div data-flow="scoring">
            <ExecutionScoreCard executionId={executionFlow.executionId} />
          </div>
        )}

        {/* Error States */}
        {executionFlow?.status === 'failed' && (
          <div data-flow="error" className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="text-sm font-medium text-red-800 mb-2">Execution Failed</div>
            {executionFlow.executionSteps.some(step => step.errorMessage) ? (
              <div className="space-y-2">
                {executionFlow.executionSteps
                  .filter(step => step.errorMessage)
                  .map((step, idx) => (
                    <div key={idx} className="text-sm">
                      <div className="font-medium text-red-700">{step.agentType} Error:</div>
                      <div className="text-red-600 mt-1">{step.errorMessage}</div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="text-sm text-red-700">An error occurred during execution</div>
            )}
          </div>
        )}

        {executionFlow?.status === 'blocked' && (
          <div data-flow="blocked" className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="text-sm font-medium text-orange-800 mb-2">Execution Blocked</div>
            <div className="text-sm text-orange-700">
              This execution was blocked due to content policy or safety restrictions.
            </div>
            {executionFlow.executionSteps.some(step => step.errorMessage) && (
              <div className="mt-2 text-sm text-orange-600">
                {executionFlow.executionSteps.find(step => step.errorMessage)?.errorMessage}
              </div>
            )}
          </div>
        )}

        {showClarificationDialog && executionFlow && (
          <ClarificationDialog
            executionId={executionFlow.executionId}
            reason={clarificationData!.reason}
            originalQuery={clarificationData!.originalQuery}
            questions={clarificationData!.questions}
            onSubmit={handleClarificationSubmit}
            onCancel={handleClarificationCancel}
          />
        )}

        {executionFlow?.status === 'needsClarification' && !showClarificationDialog && (
          <div data-flow="clarification" className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="text-sm font-medium text-yellow-800 mb-2">Needs Clarification</div>
            <div className="text-sm text-yellow-700">
              {clarificationReceived
                ? 'Clarification submitted. Resuming...'
                : 'The system needs additional information to complete this request.'}
            </div>
          </div>
        )}

        {executionFlow?.status === 'noData' && (
          <div data-flow="no-data">
            <NoDataCard
              status={executionFlow.finalResponse?.status as 'no_data' | 'no_relevant_data' | 'no_relevant_results' || 'no_data'}
              message={executionFlow.finalResponse?.message}
              answer={executionFlow.finalResponse?.answer}
            />
          </div>
        )}

        {/* Loading indicator at the bottom */}
        {isProviderLoading && !executionFlow?.finalResponse && (
          <div data-flow="loading" className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            <span className="ml-2 text-gray-600">Processing...</span>
          </div>
        )}
      </div>
    </div>
  );
};
